import { Component, OnInit, Inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { FastprisaftaleDto } from 'src/app/api/models/OK/Egis/Service/Models/DTO/fastprisaftale-dto';
import { FastPrisAddEditDialogData } from '../../../models';
import { MAX_INT32_VAL } from 'src/app/constants';
import { NotificationService } from 'src/app/notifications/services/notification.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
    selector: 'app-fastpris-add-edit',
    templateUrl: './fastpris-add-edit.component.html',
    styleUrls: ['./fastpris-add-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FastprisAddEditComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  fastPrisAftale: FastprisaftaleDto;
  editMode: Boolean = false;
  minEndDate: Date = new Date();
  today: Date = new Date();
  tomorrow: Date = new Date();
  existingFastpriser: FastprisaftaleDto[];
  isPrivatKunde: boolean = false;

  unsubscribe$ = new Subject<void>();

  get fastPris() {
    return this.form.get('fastPris');
  }
  get fixedPriceStartDate() {
    return this.form.get('fixedPriceStartDate');
  }
  get fixedPriceEndDate() {
    return this.form.get('fixedPriceEndDate');
  }
  constructor(
    private fb: UntypedFormBuilder,
    private dialog: MatDialogRef<FastprisAddEditComponent>,
    private notifications: NotificationService,
    @Inject(MAT_DIALOG_DATA) public data: FastPrisAddEditDialogData
  ) {
    this.tomorrow.setDate(this.today.getDate() + 1);
  }

  ngOnInit(): void {
    this.editMode = this.data.editMode;
    this.isPrivatKunde = this.data.isPrivatKunde;
    this.existingFastpriser = this.data.existingFastpriser;
    this.minEndDate = this.tomorrow;

    this.form = this.fb.group({
      fastPris: [null, [Validators.required, Validators.min(0), Validators.max(MAX_INT32_VAL)]],
      fixedPriceStartDate: [this.tomorrow, Validators.required],
      fixedPriceEndDate: [],
    });

    this.fixedPriceStartDate.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe((val) => {
      if (val && val > this.minEndDate) {
        this.minEndDate = val;
      }
    });

    if (this.editMode) {
      this.initializeEdit();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  initializeEdit() {
    this.form.markAllAsTouched();

    this.fastPrisAftale = this.data.fastprisAftale;
    let self = this.existingFastpriser.indexOf(this.fastPrisAftale);
    if (self >= 0) {
      this.existingFastpriser.splice(self, 1);
    }
    this.fastPris.setValue(this.isPrivatKunde ? this.fastPrisAftale.fastPrisInclMoms : this.fastPrisAftale.fastPrisExclMoms);
    this.fixedPriceStartDate.setValue(this.fastPrisAftale.startDate);
    this.fixedPriceEndDate.setValue(this.fastPrisAftale.endDate);
    if (new Date(this.fastPrisAftale.startDate) < this.today) {
      this.fixedPriceStartDate.disable();
      this.fastPris.disable();
    }
  }

  save() {
    if (this.isPrivatKunde) {
      this.fastPrisAftale = {
        fastPrisInclMoms: this.fastPris.value,
        startDate: this.fixedPriceStartDate.value,
        endDate: this.fixedPriceEndDate.value,
      };
    } else {
      this.fastPrisAftale = {
        fastPrisExclMoms: this.fastPris.value,
        startDate: this.fixedPriceStartDate.value,
        endDate: this.fixedPriceEndDate.value,
      };
    }

    if (this.existingFastpriser.length > 0 && this.existingFastpriser.some((x) => this.dateRangeOverlaps(x, this.fastPrisAftale))) {
      this.notifications.warning('Fastprisaftalen overlapper med en anden fastpris, og kan ikke gemmes, ret venligst intervallet til');
    } else {
      this.dialog.close(this.fastPrisAftale);
    }
  }

  cancel() {
    this.dialog.close(null);
  }

  private dateRangeOverlaps(existing: FastprisaftaleDto, created: FastprisaftaleDto): boolean {
    let a_start = new Date(existing.startDate),
      b_start = new Date(created.startDate),
      a_end = existing.endDate ? new Date(existing.endDate) : new Date('9999-12-31T23:59:59'),
      b_end = created.endDate ? new Date(created.endDate) : new Date('9999-12-31T23:59:59');
    b_start.setHours(0, 0, 0, 0);

    if (a_start <= b_start && b_start <= a_end) return true; // b starts in a
    if (a_start <= b_end && b_end <= a_end) return true; // b ends in a
    if (b_start < a_start && a_end < b_end) return true; // a in b
    return false;
  }
}
